// 🔁 Relances de soumissions à approuver — rien n'est envoyé sans confirmation.
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Send, XCircle, RefreshCw } from 'lucide-react';
import { LIBELLES_RELANCE } from '@/components/crm/SuiviClient';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-CA') : '—');
const fmtMontant = (v) => `${(v || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
const detail = (e) => e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Erreur';

function LigneRelance({ r, onEnvoyer, onIgnorer }) {
  const [ouvert, setOuvert] = useState(false);
  return (
    <Card data-testid={`relance-${r.id}`}>
      <CardContent className="pt-4 space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-semibold" data-testid={`relance-${r.id}-client`}>{[r.client?.prenom, r.client?.nom].filter(Boolean).join(' ') || 'Client'} · {r.numero}</p>
            <p className="text-sm text-slate-600">
              {fmtMontant(r.montant)} · envoyée le {fmtDate(r.date_envoi_soumission)} · <strong data-testid={`relance-${r.id}-jours`}>{r.jours_sans_ouverture} jour(s)</strong> sans ouverture · {r.email || 'aucun courriel'}
            </p>
          </div>
          {r.statut === 'proposee' ? (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => onEnvoyer(r)} data-testid={`relance-${r.id}-envoyer`}><Send className="w-4 h-4 mr-1" />Envoyer</Button>
              <Button size="sm" variant="outline" onClick={() => onIgnorer(r)} data-testid={`relance-${r.id}-ignorer`}><XCircle className="w-4 h-4 mr-1" />Ignorer</Button>
            </div>
          ) : (
            <span className="text-sm text-slate-600" data-testid={`relance-${r.id}-statut`}>{LIBELLES_RELANCE[r.statut] || r.statut}{r.raison_annulation ? ` — ${r.raison_annulation}` : ''}</span>
          )}
        </div>
        <button type="button" className="text-xs text-blue-700 underline" onClick={() => setOuvert((o) => !o)} data-testid={`relance-${r.id}-apercu-toggle`}>
          {ouvert ? "Masquer l'aperçu du courriel" : "Aperçu du courriel"}
        </button>
        {ouvert && (
          <pre className="whitespace-pre-wrap text-xs bg-slate-50 border rounded p-3" data-testid={`relance-${r.id}-apercu`}>
            {(r.contenu_envoye || r.contenu_prevu)?.sujet}{'\n\n'}{(r.contenu_envoye || r.contenu_prevu)?.texte}
          </pre>
        )}
      </CardContent>
    </Card>
  );
}

export default function RelancesSoumissions() {
  const [relances, setRelances] = useState([]);
  const [statut, setStatut] = useState('proposee');
  const [confirm, setConfirm] = useState(null); // {type:'envoyer'|'ignorer'|'tout', relance}
  const [motif, setMotif] = useState('');
  const [chargement, setChargement] = useState(false);

  const charger = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/relances-soumissions`, { params: { statut } });
      setRelances(r.data.relances || []);
    } catch (e) { toast.error(detail(e)); }
  }, [statut]);
  useEffect(() => { charger(); }, [charger]);

  const executer = async () => {
    setChargement(true);
    try {
      const r = await axios.post(`${API_URL}/api/relances-soumissions/executer`);
      toast.success(`Détection terminée : ${r.data.proposees || 0} proposition(s), ${r.data.annulees_auto || 0} annulée(s)`);
      charger();
    } catch (e) { toast.error(detail(e)); } finally { setChargement(false); }
  };

  const confirmer = async () => {
    const c = confirm; setConfirm(null);
    try {
      if (c.type === 'envoyer') {
        const r = await axios.post(`${API_URL}/api/relances-soumissions/${c.relance.id}/envoyer`);
        toast.success(r.data.statut === 'envoyee' ? `Relance envoyée à ${c.relance.email}` : `Relance annulée : ${r.data.raison}`);
      } else if (c.type === 'ignorer') {
        await axios.post(`${API_URL}/api/relances-soumissions/${c.relance.id}/ignorer`, { motif: motif || null });
        toast.success('Relance ignorée');
      } else {
        const r = await axios.post(`${API_URL}/api/relances-soumissions/tout-envoyer`);
        toast.success(`${r.data.envoyees} relance(s) envoyée(s)`);
      }
    } catch (e) { toast.error(detail(e)); }
    setMotif(''); charger();
  };

  const nbProposees = relances.filter((r) => r.statut === 'proposee').length;
  return (
    <div className="p-6 space-y-4" data-testid="page-relances-soumissions">
      <Card>
        <CardHeader>
          <CardTitle>Relances à approuver</CardTitle>
          <CardDescription>Soumissions envoyées et jamais ouvertes par le client après le délai configuré. Aucune relance n'est envoyée sans votre approbation.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2">
          {['proposee', 'envoyee', 'ignoree', 'annulee_auto'].map((s) => (
            <Button key={s} size="sm" variant={statut === s ? 'default' : 'outline'} onClick={() => setStatut(s)} data-testid={`filtre-${s}`}>{LIBELLES_RELANCE[s]}</Button>
          ))}
          <span className="flex-1" />
          <Button size="sm" variant="outline" onClick={executer} disabled={chargement} data-testid="relances-executer"><RefreshCw className="w-4 h-4 mr-1" />Détecter maintenant</Button>
          {statut === 'proposee' && nbProposees > 0 && (
            <Button size="sm" onClick={() => setConfirm({ type: 'tout' })} data-testid="relances-tout-envoyer"><Send className="w-4 h-4 mr-1" />Tout envoyer ({nbProposees})</Button>
          )}
        </CardContent>
      </Card>
      {relances.length === 0 && <p className="text-sm text-slate-500" data-testid="relances-vide">Aucune relance dans cette catégorie.</p>}
      {relances.map((r) => <LigneRelance key={r.id} r={r} onEnvoyer={(x) => setConfirm({ type: 'envoyer', relance: x })} onIgnorer={(x) => setConfirm({ type: 'ignorer', relance: x })} />)}

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="dialog-confirmation-relance">
          <DialogHeader>
            <DialogTitle>
              {confirm?.type === 'envoyer' && `Envoyer la relance à ${confirm.relance.email} ?`}
              {confirm?.type === 'ignorer' && `Ignorer la relance de ${confirm.relance.numero} ?`}
              {confirm?.type === 'tout' && `Envoyer les ${nbProposees} relances proposées ?`}
            </DialogTitle>
            <DialogDescription>
              {confirm?.type === 'ignorer' ? 'Aucun courriel ne sera envoyé. Motif optionnel :' : 'Le courriel de relance sera envoyé au client avec le lien actif de sa soumission.'}
            </DialogDescription>
          </DialogHeader>
          {confirm?.type === 'ignorer' && <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex. : client joint par téléphone" data-testid="relance-motif" />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} data-testid="confirmation-annuler">Annuler</Button>
            <Button onClick={confirmer} data-testid="confirmation-confirmer">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
